<template>
  <div class="p-4 h-full overflow-y-auto">
    <!-- Report Creation Modal -->
    <ReportCreationModal
      v-if="showCreateModal"
      :organization-id="organizationId"
      :report-type="stats.nextReportType || 'month_1'"
      @close="showCreateModal = false"
      @reportCreated="handleReportCreated"
    />

    <!-- Report Edit Modal -->
    <ReportEditModal
      v-if="showEditModal && editingReport"
      :organization-id="organizationId"
      :report-id="editingReport.id"
      :report="editingReport"
      @close="closeEditModal"
      @reportUpdated="handleReportUpdated"
    />

    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900 mb-2">Monthly Reports</h2>
      <p class="text-sm text-gray-600">{{ organizationName || 'Organization' }}</p>
    </div>

    <!-- Report Statistics -->
    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <div class="grid grid-cols-2 gap-4">
        <div>
          <p class="text-xs text-gray-600 uppercase tracking-wide">Total Reports</p>
          <p class="text-2xl font-bold text-gray-900">{{ stats.totalReports || 0 }}</p>
        </div>
        <div>
          <p class="text-xs text-gray-600 uppercase tracking-wide">Last Report</p>
          <p class="text-sm font-medium text-gray-900">
            {{ formatDate(stats.lastReportDate) || 'Never' }}
          </p>
        </div>
        <div>
          <p class="text-xs text-gray-600 uppercase tracking-wide">Next Type</p>
          <p class="text-sm font-medium text-gray-900">
            {{ formatReportType(stats.nextReportType) }}
          </p>
        </div>
        <div>
          <p class="text-xs text-gray-600 uppercase tracking-wide">Status</p>
          <div class="flex items-center">
            <span
              v-if="stats.reportsSkipped"
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
              Skipped
            </span>
            <span
              v-else-if="stats.needsReport"
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
              Needs Report
            </span>
            <span
              v-else
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              Up to Date
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex space-x-3 mb-6">
      <button
        @click="openCreateModal"
        :disabled="isLoading || stats.reportsSkipped"
        class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
               disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
        <i class="fas fa-plus mr-2" />
        Create {{ formatReportType(stats.nextReportType) }} Report
      </button>

      <button
        @click="toggleSkipReports"
        :disabled="isLoading"
        class="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50
               disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
        <i :class="stats.reportsSkipped ? 'fas fa-play' : 'fas fa-pause'" class="mr-2" />
        {{ stats.reportsSkipped ? 'Resume' : 'Skip' }} Reports
      </button>
    </div>

    <!-- Error Alert -->
    <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
    </div>

    <!-- Reports List -->
    <div v-else-if="reports.length > 0" class="space-y-4">
      <h3 class="text-lg font-semibold text-gray-900 mb-3">Report History</h3>

      <div
        v-for="report in reports"
        :key="report.id"
        class="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h4 class="text-lg font-medium text-gray-900">{{ report.title }}</h4>
              <p class="text-sm text-gray-500 mt-1">
                {{ formatReportType(report.reportType) }} •
                Created {{ formatDate(report.createdAt) }}
                <span v-if="report.createdBy">by {{ report.createdBy }}</span>
              </p>

              <div class="flex flex-wrap gap-2 mt-2">
                <span
                  v-if="report.isPublic"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  <i class="fas fa-globe mr-1" />
                  Public
                </span>
                <span
                  v-else
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                  <i class="fas fa-lock mr-1" />
                  Private
                </span>

                <span
                  v-if="report.viewCount > 0"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  <i class="fas fa-eye mr-1" />
                  {{ report.viewCount }} views
                </span>

                <span
                  v-if="report.includeMomentsStats"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                  <i class="fas fa-chart-line mr-1" />
                  Includes Stats
                </span>
              </div>

              <!-- Public URL Display -->
              <div v-if="report.isPublic && report.publicUrl" class="mt-3 p-2 bg-blue-50 rounded">
                <label class="text-xs text-gray-600 font-medium">Public URL:</label>
                <div class="flex items-center mt-1 gap-2">
                  <input
                    :value="report.publicUrl"
                    readonly
                    class="flex-1 text-xs px-2 py-1 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    @click="$event.target.select()">
                  <button
                    @click="copyPublicUrl(report)"
                    class="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                    <i class="fas fa-copy" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Report Actions -->
          <div class="flex flex-wrap gap-2 mt-4">
            <button
              @click="editReport(report)"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors duration-200">
              <i class="fas fa-edit mr-1" />
              Edit
            </button>

            <button
              v-if="report.is_public"
              @click="copyPublicUrl(report)"
              class="px-3 py-1 text-sm border border-blue-300 text-blue-600 rounded hover:bg-blue-50 transition-colors duration-200">
              <i class="fas fa-link mr-1" />
              Copy Link
            </button>

            <button
              @click="togglePublic(report)"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors duration-200">
              <i :class="report.is_public ? 'fas fa-eye-slash' : 'fas fa-eye'" class="mr-1" />
              Make {{ report.is_public ? 'Private' : 'Public' }}
            </button>

            <button
              @click="exportPdf(report)"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors duration-200">
              <i class="fas fa-file-pdf mr-1" />
              Export PDF
            </button>

            <button
              @click="deleteReport(report)"
              class="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors duration-200">
              <i class="fas fa-trash mr-1" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-8">
      <i class="fas fa-file-alt text-4xl text-gray-300 mb-3" />
      <p class="text-gray-500">No reports yet</p>
      <p class="text-sm text-gray-400 mt-1">Create your first monthly report to get started</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import axios from 'axios'
import mdtoast from '@dmuy/toast'
import consumer from '@/channels/consumer'
import ReportCreationModal from './monthly_report_creation_modal.vue'
import ReportEditModal from './monthly_report_edit_modal.vue'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  },
  organizationName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['reportCreated', 'reportUpdated', 'reportDeleted'])

// State
const reports = ref([])
const stats = ref({})
const isLoading = ref(false)
const error = ref(null)
const showCreateModal = ref(false)
const showEditModal = ref(false)
const editingReport = ref(null)
let subscription = null

// Methods
const fetchReports = async () => {
  isLoading.value = true
  error.value = null

  try {
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/monthly_reports`)
    reports.value = response.data.reports

    stats.value = response.data.stats
  } catch (err) {
    error.value = 'Failed to load reports. Please try again.'
    console.error('Error fetching reports:', err)
  } finally {
    isLoading.value = false
  }
}

const openCreateModal = () => {
  showCreateModal.value = true
}

const handleReportCreated = (report) => {
  showCreateModal.value = false
  mdtoast.success('Report created successfully', { duration: 3000 })
  emit('reportCreated', report)
  fetchReports()
}

const editReport = (report) => {
  editingReport.value = report
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  editingReport.value = null
}

const handleReportUpdated = (updatedReport) => {
  // Update the report in the list
  const index = reports.value.findIndex(r => r.id === updatedReport.id)
  if (index !== -1) {
    reports.value[index] = updatedReport
  }
  closeEditModal()
  mdtoast.success('Report updated successfully', { duration: 3000 })
  emit('reportUpdated', updatedReport)
}

const togglePublic = async (report) => {
  try {
    const response = await axios.post(
      `/ajax/organizations/${props.organizationId}/monthly_reports/${report.id}/toggle_public`
    )
    report.isPublic = response.data.isPublic
    report.publicUrl = response.data.publicUrl
    mdtoast.success(response.data.message, { duration: 3000 })
  } catch (err) {
    mdtoast.error('Failed to update report visibility', { duration: 3000 })
  }
}

const copyPublicUrl = (report) => {
  if (!report.publicUrl) return

  navigator.clipboard.writeText(report.publicUrl)
  mdtoast.success('Public URL copied to clipboard', { duration: 3000 })
}

const exportPdf = (report) => {
  window.open(
    `/ajax/organizations/${props.organizationId}/monthly_reports/${report.id}/export_pdf`,
    '_blank'
  )
}

const deleteReport = async (report) => {
  if (!confirm(`Are you sure you want to delete "${report.title}"?`)) {
    return
  }

  try {
    await axios.delete(
      `/ajax/organizations/${props.organizationId}/monthly_reports/${report.id}`
    )
    mdtoast.success('Report deleted successfully', { duration: 3000 })
    emit('reportDeleted', report)
    fetchReports()
  } catch (err) {
    mdtoast.error('Failed to delete report', { duration: 3000 })
  }
}

const toggleSkipReports = async () => {
  try {
    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/update_feature`,
      { feature: 'reports_skipped', value: !stats.value.reports_skipped }
    )
    stats.value.reports_skipped = !stats.value.reports_skipped
    mdtoast.success(
      stats.value.reports_skipped ? 'Reports skipped' : 'Reports resumed',
      { duration: 3000 }
    )
  } catch (err) {
    mdtoast.error('Failed to update report settings', { duration: 3000 })
  }
}

const formatDate = (dateString) => {
  if (!dateString) return null
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const formatReportType = (type) => {
  if (!type) return 'Month 1'
  const typeMap = {
    month_1: 'Month 1',
    month_2: 'Month 2',
    month_3_plus: 'Month 3+'
  }
  return typeMap[type] || type
}

// ActionCable subscription
const setupSubscription = () => {
  subscription = consumer.subscriptions.create(
    {
      channel: 'MonthlyReportsChannel',
      organization_id: props.organizationId
    },
    {
      connected() {
        console.log('Connected to MonthlyReportsChannel')
      },
      disconnected() {
        console.log('Disconnected from MonthlyReportsChannel')
      },
      received(data) {
        handleChannelEvent(data)
      }
    }
  )
}

const handleChannelEvent = (data) => {
  switch(data.event) {
    case 'report_created':
      // Add the new report to the list
      reports.value.unshift(data.report)
      // Update stats
      stats.value.totalReports = (stats.value.totalReports || 0) + 1
      stats.value.lastReportDate = data.report.createdAt
      mdtoast.info('New report created', { duration: 2000 })
      break

    case 'report_updated':
      // Update the report in the list
      const updateIndex = reports.value.findIndex(r => r.id === data.report.id)
      if (updateIndex !== -1) {
        reports.value[updateIndex] = data.report
      }
      break

    case 'report_visibility_changed':
      // Update the report visibility
      const visibilityIndex = reports.value.findIndex(r => r.id === data.report.id)
      if (visibilityIndex !== -1) {
        reports.value[visibilityIndex] = data.report
      }
      break

    case 'report_deleted':
      // Remove the report from the list
      reports.value = reports.value.filter(r => r.id !== data.report_id)
      // Update stats
      stats.value.totalReports = Math.max(0, (stats.value.totalReports || 0) - 1)
      mdtoast.info('Report deleted', { duration: 2000 })
      break
  }
}

// Lifecycle
onMounted(() => {
  fetchReports()
  setupSubscription()
})

onUnmounted(() => {
  if (subscription) {
    subscription.unsubscribe()
  }
})
</script>